// Types and small helpers for the knowledge-base components (design
// 2026-10-02-kennisbank-ontwerp.md §4–§6, §10). The wire types come from
// `@goblic/shared` (kb/dto.ts); this file only adds the few view shapes the
// components need on top (a space with its label, the editor's save
// payload) and display helpers.

import {
  kbFileName,
  kbFolderOf,
  kbPathError,
  type KbAgentWrite,
  type KbAuthor,
  type KbConflictDetails,
  type KbFolderPolicy,
  type KbFrontmatter,
  type KbSpace,
  type KbTreeEntry,
} from '@goblic/shared'

export type {
  KbAgentWrite,
  KbAuthor,
  KbFolderPolicy,
  KbFolderPolicyListResponse,
  KbFrontmatter,
  KbPage,
  KbProposal,
  KbRevision,
  KbRevisionStatus,
  KbRevisionSummary,
  KbSearchHit,
  KbSpace,
  KbStale,
  KbTreeEntry,
  KbWriteResult,
} from '@goblic/shared'

/** `kbSlug`: folder name for a role/agent (`roles/<slug>/`), as the memory migration uses. */
export { kbFileName, kbFolderOf, kbSlug } from '@goblic/shared'

/** Resolves a `space` value (`workspace` / `project:<id>`) to a display label. */
export type KbSpaceLabel = (space: string) => string

/** A space shown in the tree: the workspace, or one project. */
export interface KbTreeSpace {
  space: KbSpace
  label: string
  entries: KbTreeEntry[]
  /** Stored folder policies plus built-in defaults, to mark folders. */
  policies?: KbFolderPolicy[]
}

/** What the editor hands to `onSave`; maps onto `PUT …/pages` (§6.1). */
export interface KbSavePayload {
  path: string
  /** Full document: frontmatter block + Markdown body. */
  content: string
  message: string | undefined
  /** Revision the edit is based on; undefined for a new page. */
  baseRev: number | undefined
}

/** `details` of a 409 KB_CONFLICT: the current rev and the page as it is now. */
export type KbConflict = KbConflictDetails

// ---------------------------------------------------------------------------
// Helpers

/**
 * Path check for the editor and "new page" input: the shared rules
 * (`kbPathError`), with a friendlier message for an empty field. The API
 * stays authoritative.
 */
export function validateKbPath(path: string): string | null {
  if (!path) return 'Enter a path.'
  return kbPathError(path)
}

/** Kept for older call sites; same as `kbFileName`. */
export const kbBaseName = kbFileName

/**
 * Effective policy for a path: longest matching folder prefix wins (§4);
 * stored policies take precedence over defaults for the same folder.
 */
export function kbEffectivePolicy(
  policies: KbFolderPolicy[] | undefined,
  path: string
): KbFolderPolicy | null {
  let best: KbFolderPolicy | null = null
  for (const policy of policies ?? []) {
    if (
      path.startsWith(policy.folder) &&
      (!best || policy.folder.length > best.folder.length)
    )
      best = policy
  }
  return best
}

export const KB_AGENT_WRITE_LABEL: Record<KbAgentWrite, string> = {
  direct: 'Agents write directly',
  proposal: 'Agents propose, a person approves',
  read_only: 'Agents only read',
}

/** Display name for an author: the person, else the agent. */
export function kbAuthorName(author: Partial<KbAuthor> | undefined | null): string {
  if (!author) return 'Unknown'
  if (author.userName) return author.userName
  if (author.agentName) return `Agent ${author.agentName}`
  if (author.runId) return 'An agent'
  if (author.userId) return 'A person'
  return 'Goblic'
}

function yamlScalar(value: unknown): string {
  // JSON is valid YAML flow syntax, so strings/arrays/objects stay exact.
  return JSON.stringify(value)
}

/**
 * Serialises frontmatter + body into the full page document the API parses
 * (§5). Known fields come first in a stable order; unknown fields are kept.
 * Empty optional fields are left out.
 */
export function serializeKbDocument(
  frontmatter: KbFrontmatter | Record<string, unknown>,
  body: string
): string {
  const fields = frontmatter as Record<string, unknown>
  const order = [
    'title',
    'summary',
    'applies_to',
    'tags',
    'always_load',
    'verified_at',
  ]
  const keys = [
    ...order.filter((key) => key in fields),
    ...Object.keys(fields).filter((key) => !order.includes(key)),
  ]
  const lines: string[] = []
  for (const key of keys) {
    const value = fields[key]
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      lines.push(`${key}:`)
      for (const item of value) lines.push(`  - ${yamlScalar(item)}`)
    } else if (key === 'always_load' && value === false) {
      continue
    } else {
      lines.push(`${key}: ${yamlScalar(value)}`)
    }
  }
  if (lines.length === 0) {
    // A body that itself starts with a `---` line would be read back as a
    // frontmatter block; an explicit empty block keeps it body.
    return /^\uFEFF?---[ \t]*\r?\n/.test(body) ? `---\n{}\n---\n${body}` : body
  }
  return `---\n${lines.join('\n')}\n---\n${body}`
}
